import { ElementType, FC, useMemo } from 'react'
import { ActivityDurationBadge, HighlightedText } from 'react-common'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import cn from 'classnames'
import { CourseResultBadge } from '@/components/CourseResultBadge/CourseResultBadge'
import { CourseDateWithIcon } from '@/components/courses/CourseDateWithIcon/CourseDateWithIcon'
import { ProgressBar } from '@/components/ProgressBar/ProgressBar'
import { useIsMobileNew } from '@/hooks/useIsMobileNew'
import { FrontActivityPart } from '@/store/slices/aggregateSlice/types/FrontActivityPart.type'
import { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'
import { isOpenCatalog } from '@/utils/isOpenCatalog'
import styles from './Course.module.scss'
import { DraftActivity } from './DraftActivity/DraftActivity'
import { LockedActivityPart } from './LockedActivityPart/LockedActivityPart'
import { NotificationIndicator } from './NotificationIndicator/NotificationIndicator'
import { SubActivityCountBadge } from './SubActivityCountBadge'

type CourseComponentProps = {
  to?: string
  activityPart?: FrontActivityPart<FrontActivityWithAggregate>
  id: string
  name: string
  duration?: number | null
  badge?: string
  isFinished: boolean
  showDetails: boolean
  grade?: string
  score?: string
  coverUrl?: string | null
  isInProgress: boolean
  isInGrading?: boolean
  progress?: number
  finishedDate?: string
  startedDate?: string
  submittedDate?: string
  dueDate?: string
  availabilityDate?: string
  dotBackgroundColor?: string
  coverBackgroundColor: string
  isNotStarted: boolean
  subActivityCount?: number
  showActivityCount: boolean
  isLocked?: boolean
  isDraft?: boolean
  collections?: string
  code?: string
  showCode?: boolean
  textToBeHighlighted?: string
  isPassed?: boolean
  state?: string
  showPassed?: boolean
  announcementChannelId?: string
}

export const CourseComponent: FC<CourseComponentProps> = ({
  to,
  id,
  name,
  duration,
  badge,
  isFinished,
  showDetails,
  grade,
  score,
  coverUrl,
  isInProgress,
  isInGrading,
  progress,
  finishedDate,
  startedDate,
  submittedDate,
  dueDate,
  availabilityDate,
  dotBackgroundColor,
  coverBackgroundColor,
  isNotStarted,
  subActivityCount = 0,
  showActivityCount,
  isLocked,
  isDraft,
  code,
  showCode,
  textToBeHighlighted,
  isPassed,
  state,
  showPassed,
  announcementChannelId,
}) => {
  const { t } = useTranslation()
  const isOpenCatalogPage = isOpenCatalog()

  const notStartedTextArea = useMemo(
    () => (
      <div className={cn(styles.cardBottom)}>
        <div className={styles.resultsInfo}>
          <CourseDateWithIcon text={t('courseStates.notStarted')} icon="HourglassMediumStroke" />
        </div>
      </div>
    ),
    [t],
  )

  const inProgressTextArea = useMemo(
    () => (
      <div className={cn(styles.cardBottom, styles.withProgressBar)}>
        <div className={styles.resultsInfo}>
          <CourseDateWithIcon text={t('courseStates.started')} value={startedDate} />
        </div>
        <ProgressBar value={progress} className={styles.progressBar} />
      </div>
    ),
    [startedDate, progress, t],
  )

  const dueDateArea = useMemo(
    () => (
      <div className={cn(styles.cardBottom, { [styles.withProgressBar]: startedDate })}>
        <div className={styles.resultsInfo}>
          <CourseDateWithIcon
            text={t('courseDetails.dueDate')}
            icon={startedDate ? undefined : 'CalendarMediumStroke'}
            value={dueDate!}
          />
        </div>

        {startedDate && <ProgressBar value={progress} />}
      </div>
    ),
    [startedDate, progress, dueDate, t],
  )

  const finishedTextArea = useMemo(
    () => (
      <div className={cn(styles.cardBottom)}>
        <div className={styles.resultsInfo}>
          <CourseDateWithIcon text={t('courseStates.completed')} value={finishedDate!} />
        </div>
      </div>
    ),
    [finishedDate, t],
  )

  const submittedTextArea = useMemo(
    () => (
      <div className={cn(styles.cardBottom, { [styles.withProgressBar]: true })}>
        <div className={styles.resultsInfo}>
          {t('courseStates.submitted')}: {submittedDate}
        </div>

        <ProgressBar value={progress} />
      </div>
    ),
    [t, submittedDate, progress],
  )

  const availabilityDateArea = useMemo(
    () => (
      <div className={styles.cardBottom}>
        <div className={cn(styles.resultsInfo)}>
          <CourseDateWithIcon
            text={t('courseDetails.availability')}
            icon="CalendarMediumStroke"
            value={availabilityDate!}
          />
        </div>
      </div>
    ),
    [availabilityDate, t],
  )

  const dateAndProgress = useMemo(() => {
    if (isNotStarted) {
      if (availabilityDate) return availabilityDateArea
      if (dueDate) return dueDateArea
      return notStartedTextArea
    }
    if (isInGrading) return submittedTextArea
    if (isInProgress) return inProgressTextArea
    if (isFinished) return finishedTextArea
    if (dueDate) return dueDateArea
  }, [
    notStartedTextArea,
    availabilityDate,
    availabilityDateArea,
    dueDate,
    dueDateArea,
    finishedTextArea,
    inProgressTextArea,
    isFinished,
    isInProgress,
    isNotStarted,
    isInGrading,
    submittedTextArea,
  ])

  const isLink = !!to

  const Component: ElementType = isLink ? Link : 'div'

  const backgroundImageOrColor = coverUrl
    ? { backgroundImage: `url(${coverUrl})` }
    : { backgroundColor: coverBackgroundColor }

  const showNotification = !!announcementChannelId && !isOpenCatalogPage

  const isMobile = useIsMobileNew()

  return (
    <Component className={cn(styles.courseLink, isFinished)} {...(isLink ? { to } : {})}>
      <article className={styles.course}>
        <header className={styles.header}>
          <h2 title={name} className={styles.title} data-qa={`card-title-${id}`}>
            <HighlightedText text={name} textToBeHighlighted={textToBeHighlighted ?? ''} />
          </h2>
          {showCode && (
            <div className={styles.code}>
              <HighlightedText text={code ?? ''} textToBeHighlighted={textToBeHighlighted ?? ''} />
            </div>
          )}
        </header>
        <section className={styles.card}>
          <div className={styles.cover} style={backgroundImageOrColor}>
            <div className={styles.subActivity}>
              {showActivityCount && <SubActivityCountBadge subActivityCount={subActivityCount} />}
              {!isMobile && <ActivityDurationBadge short color="neutralWhite" duration={duration} />}
            </div>
            {isLocked && <LockedActivityPart />}
            {isDraft && <DraftActivity />}
          </div>
          {showDetails && (
            <div className={styles.details}>
              <div className={styles.grade}>
                <dl className={styles.text}>
                  <dt className={styles.label} data-qa={`card-grade-title-${id}`}>
                    {t('courseCard.grade')}
                  </dt>
                  <dd className={styles.value} data-qa={`card-grade-${id}`}>
                    {grade ?? '-'}
                  </dd>
                </dl>
              </div>
              <div className={styles.score}>
                <dl className={styles.text}>
                  <dt className={styles.label} data-qa={`card-score-title-${id}`}>
                    {t('courseCard.score')}
                  </dt>
                  <dd className={styles.value} data-qa={`card-score-${id}`}>
                    {score}
                  </dd>
                </dl>
              </div>
              <div className={styles.action}>
                <button className={styles.button} data-qa={`go-to-details-button-${id}`}>
                  {t('courseCard.goToDetails')}
                </button>
              </div>
            </div>
          )}
        </section>
        <section className={styles.footer}>{dateAndProgress}</section>
        {((score !== null && badge) || showNotification) && (
          <div className={styles.badge}>
            {score !== null && badge && (
              <CourseResultBadge
                grade={grade}
                label={badge}
                dotStyles={{ backgroundColor: dotBackgroundColor! }}
                className={styles.chip}
                isPassed={isPassed}
                state={state}
                score={score}
                showPassed={showPassed}
              />
            )}
            {showNotification && (
              <div className={styles.notification}>
                <NotificationIndicator announcementChannelId={announcementChannelId} />
              </div>
            )}
          </div>
        )}
      </article>
    </Component>
  )
}
